import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Pencil, 
  Trash2, 
  User, 
  Calendar, 
  Clock, 
  FileText, 
  Sparkles, 
  Copy 
} from 'lucide-react';
import { editorService } from '../services/api';
import { useBrandedDialog } from './BrandedDialog';
import './EditorDetail.css';

const getStyleColors = (style) => {
  const map = {
    'Formal': { bg: '#e0f2fe', color: '#0284c7' },
    'Casual': { bg: '#ccfbf1', color: '#0d9488' },
    'Technical': { bg: '#f1f5f9', color: '#475569' },
    'Creative': { bg: '#fce7f3', color: '#db2777' },
    'Journalistic': { bg: '#ffedd5', color: '#ea580c' },
    'Academic': { bg: '#f3e8ff', color: '#9333ea' },
  };
  return map[style] || { bg: '#f1f5f9', color: '#475569' };
};

const EditorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { alert, confirm } = useBrandedDialog();
  const [editor, setEditor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchEditor = async () => {
      try {
        setLoading(true);
        const response = await editorService.getById(id);
        setEditor(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to load Editor details');
        console.error('Error fetching Editor:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEditor();
  }, [id]);

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete editor?',
      message: `Delete "${editor.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await editorService.delete(id);
      navigate('/editors');
    } catch (err) {
      alert({ title: 'Failed to delete editor', message: err.response?.data?.error || err.message || 'Please try again.', tone: 'danger' });
      console.error('Error deleting Editor:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    if (!name) return 'ED';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const formatAuditUser = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.userName || [value.firstName, value.lastName].filter(Boolean).join(' ') || value.name || value._id || '';
  };

  const handleCopy = () => {
    if (editor?.prompt) {
      navigator.clipboard.writeText(editor.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="app-loading-container">
        <div className="app-loading-spinner" />
        <p>Loading Editor details...</p>
      </div>
    );
  }

  if (error || !editor) {
    return (
      <div className="error">
        {error || 'Editor not found'}
        <button className="btn btn-primary" onClick={() => navigate('/editors')} style={{ marginTop: '1rem' }}>
          Back to List
        </button>
      </div>
    );
  }

  const createdDate = editor.createdDate ? formatDate(editor.createdDate) : 'Unknown';
  const updatedDate = editor.updatedAt ? formatDate(editor.updatedAt) : createdDate;
  const styleColors = getStyleColors(editor.style);

  return (
    <div className="editor-detail-page">
      <div className="editor-detail-main">
        {/* Top Actions */}
        <div className="editordetail-top-actions">
          <button className="editordetail-btn-back" onClick={() => navigate('/editors')}>
            <ArrowLeft size={16} strokeWidth={2} />
            Back to List
          </button>
          
          <div className="editordetail-top-actions-right">
            <button className="editordetail-btn-edit" onClick={() => navigate(`/editor/${id}/edit`)}>
              <Pencil size={14} strokeWidth={2} />
              Edit
            </button>
            <button className="editordetail-btn-delete" onClick={handleDelete}>
              <Trash2 size={14} strokeWidth={2} />
              Delete
            </button>
          </div>
        </div>

        {/* Profile Header */}
        <div className="editordetail-profile-header">
          <div className="editordetail-avatar" style={{ background: styleColors.bg, color: styleColors.color }}>
            {getInitials(editor.name)}
          </div>
          <div className="editordetail-profile-info">
            <div className="editordetail-name-row">
              <h1 className="editordetail-name">{editor.name}</h1>
              {editor.isActive !== false ? (
                <div className="editordetail-status-badge">
                  <span className="editordetail-status-dot"></span>
                  Active
                </div>
              ) : (
                <div className="editordetail-status-badge" style={{ background: '#fff7ed', borderColor: '#ffedd5', color: '#ea580c' }}>
                  <span className="editordetail-status-dot" style={{ background: '#ea580c' }}></span>
                  Disabled
                </div>
              )}
            </div>
            <div className="editordetail-badges-row">
              <span className="editordetail-badge" style={{ background: styleColors.bg, color: styleColors.color }}>
                {(editor.style || 'GENERAL').toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        <div className="editordetail-divider"></div>

        {/* Stats Grid */}
        <div className="editordetail-stats-grid">
          <div className="editordetail-stat-card">
            <div className="editordetail-stat-icon-wrap" style={{ color: styleColors.color, background: styleColors.bg }}>
              <User size={18} strokeWidth={2} />
            </div>
            <div className="editordetail-stat-content">
              <p className="editordetail-stat-label">LANGUAGE STYLE</p>
              <p className="editordetail-stat-value">{editor.style || 'General'}</p>
            </div>
          </div>
          <div className="editordetail-stat-card">
            <div className="editordetail-stat-icon-wrap" style={{ color: '#8b5cf6', background: '#ede9fe' }}>
              <Calendar size={18} strokeWidth={2} />
            </div>
            <div className="editordetail-stat-content">
              <p className="editordetail-stat-label">CREATED DATE</p>
              <p className="editordetail-stat-value">{createdDate}</p>
              {formatAuditUser(editor.createdBy) && (
                <p className="editordetail-stat-subvalue">By {formatAuditUser(editor.createdBy)}</p>
              )}
            </div>
          </div>
          <div className="editordetail-stat-card">
            <div className="editordetail-stat-icon-wrap" style={{ color: '#8b5cf6', background: '#ede9fe' }}>
              <Clock size={18} strokeWidth={2} />
            </div>
            <div className="editordetail-stat-content">
              <p className="editordetail-stat-label">LAST UPDATED</p>
              <p className="editordetail-stat-value">{updatedDate}</p>
              {formatAuditUser(editor.updatedBy) && (
                <p className="editordetail-stat-subvalue">By {formatAuditUser(editor.updatedBy)}</p>
              )}
            </div>
          </div>
        </div>

        {/* Description Card */}
        <div className="editordetail-full-card">
          <div className="editordetail-card-header">
            <div className="editordetail-card-title-group">
              <div className="editordetail-card-icon-wrap" style={{ color: '#8b5cf6', background: '#ede9fe' }}>
                <FileText size={18} strokeWidth={2} />
              </div>
              <h2 className="editordetail-card-title">DESCRIPTION</h2>
            </div>
          </div>
          <div className="editordetail-card-body">
            <p className="editordetail-description-text">
              {editor.description || 'No description provided.'}
            </p>
          </div>
        </div>

        {/* AI Prompt Card */}
        <div className="editordetail-full-card">
          <div className="editordetail-card-header">
            <div className="editordetail-card-title-group">
              <div className="editordetail-card-icon-wrap" style={{ color: '#a855f7', background: '#f3e8ff' }}>
                <Sparkles size={18} strokeWidth={2} />
              </div>
              <h2 className="editordetail-card-title">AI PROMPT (EDITORIAL GUIDELINES)</h2>
            </div>
            <button className="editordetail-btn-copy" onClick={handleCopy} title="Copy prompt">
              {copied ? <span style={{fontSize:'0.75rem', color:'#10b981', fontWeight:'600'}}>Copied!</span> : <Copy size={16} strokeWidth={2} />}
            </button>
          </div>
          <div className="editordetail-card-body editordetail-prompt-body">
            <div className="editordetail-prompt-box">
              {editor.prompt}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default EditorDetail;
